import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuIcon from "@mui/icons-material/Menu";
import { Toaster } from "react-hot-toast";
import { afmsWorkspaceSx, cc } from "afms/theme/commandCenterTheme";
import FixedOpsPortalSidebar from "./FixedOpsPortalSidebar";

const SIDEBAR_WIDTH = 280;

/**
 * The chrome that wraps a Fixed Ops portal app: the industrial-blue
 * FixedOpsPortalSidebar on the left (sticky column on desktop, drawer on
 * mobile) and a flex:1 content area on the right where routes render.
 *
 * Mounted ONCE at App.js level when pickSidebarVariant returns
 * "fixedOpsPortal". Page-level layouts (FixedOpsPortalLayout) handle only
 * their own header + main content — they don't pick the sidebar.
 *
 * Mirrors EnvironmentalPortalShell. The shell reuses cc.page / cc.ink
 * for the workspace surface so non-portal-aware pages render identically
 * to the AFMS / LCP / Energy portals.
 */
function FixedOpsPortalShell({ children, onSwitchSystem }) {
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery("(min-width:1200px)");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  return (
    <Box
      className="fixedops-portal-shell"
      sx={{
        ...afmsWorkspaceSx,
        display: "flex",
        minHeight: "100vh",
        bgcolor: cc.page,
        color: cc.ink,
      }}
    >
      {isDesktop ? (
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            borderRight: "1px solid rgba(255,255,255,0.08)",
            bgcolor: cc.navy,
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
            height: "100vh",
            overflowY: "auto",
          }}
        >
          <FixedOpsPortalSidebar onSwitchSystem={onSwitchSystem} />
        </Box>
      ) : (
        <>
          <IconButton
            aria-label="Open navigation"
            onClick={() => setDrawerOpen(true)}
            sx={{
              position: "fixed",
              top: 12,
              left: 12,
              zIndex: 1300,
              bgcolor: cc.navy,
              color: "#fff",
              boxShadow: "0 10px 20px rgba(6,31,54,0.18)",
              "&:hover": { bgcolor: cc.navy2 },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{
              sx: { width: SIDEBAR_WIDTH, bgcolor: cc.navy },
            }}
          >
            <FixedOpsPortalSidebar
              onNavigate={() => setDrawerOpen(false)}
              onSwitchSystem={onSwitchSystem}
            />
          </Drawer>
        </>
      )}
      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {children}
      </Box>
      <Toaster />
    </Box>
  );
}

FixedOpsPortalShell.propTypes = {
  children: PropTypes.node,
  onSwitchSystem: PropTypes.func,
};

export default FixedOpsPortalShell;
